/**
 * 계약대장 BdContract API (isBdApiEnabled 시 /common/select|save)
 */
import { isBdApiEnabled } from 'config/bdApiConfig';
import {
  cacheGet,
  cacheSet,
  commonSaveList,
  commonSelect,
  diffRowsByUid,
} from 'services/commonQuery';

export function dimsKey(tenantCode, fiscalYear) {
  return `${String(tenantCode || '').trim()}|${String(fiscalYear || '').trim()}`;
}

export function dimsParams(tenantCode, fiscalYear) {
  return {
    tenantCode: String(tenantCode || '').trim(),
    fiscalYear: Number(fiscalYear) || fiscalYear,
  };
}

function clearMod(rows) {
  return (rows || []).map((r) => ({ ...r, mod: '' }));
}

export const SAVE_Q = {
  contract: ['BdContract.insertContract', 'BdContract.updateContract', 'BdContract.deleteContract'],
  oblLink: ['BdContract.insertOblLink', 'BdContract.updateOblLink', 'BdContract.deleteOblLink'],
  extRef: ['BdContract.insertExtRef', 'BdContract.updateExtRef', 'BdContract.deleteExtRef'],
};

const contractCache = new Map();

export { isBdApiEnabled, cacheGet, cacheSet };

export async function apiLoadContractBundle(tenantCode, fiscalYear) {
  const params = dimsParams(tenantCode, fiscalYear);
  const [contracts, oblLinks, extRefs] = await Promise.all([
    commonSelect('BdContract.selectContractList', params),
    commonSelect('BdContract.selectOblLinkList', params),
    commonSelect('BdContract.selectExtRefList', params),
  ]);
  const bundle = {
    contracts: clearMod(contracts),
    oblLinks: clearMod(oblLinks),
    extRefs: clearMod(extRefs),
  };
  cacheSet(contractCache, dimsKey(tenantCode, fiscalYear), bundle);
  return bundle;
}

export async function apiSaveContractBundle(
  tenantCode,
  fiscalYear,
  { contracts, oblLinks, extRefs },
  ensureContractUid,
  ensureLinkUid,
  ensureExtUid
) {
  const key = dimsKey(tenantCode, fiscalYear);
  const params = dimsParams(tenantCode, fiscalYear);
  const prev = cacheGet(contractCache, key) || (await apiLoadContractBundle(tenantCode, fiscalYear));
  const nextC = contracts != null ? (contracts || []).filter((r) => r && r.mod !== 'D') : prev.contracts;
  const nextL = oblLinks != null ? (oblLinks || []).filter((r) => r && r.mod !== 'D') : prev.oblLinks;
  const nextE = extRefs != null ? (extRefs || []).filter((r) => r && r.mod !== 'D') : prev.extRefs;

  const dirtyP = diffRowsByUid(prev.contracts || [], nextC, 'contractUid', ensureContractUid);
  const deleted = new Set(dirtyP.filter((r) => r.mod === 'D').map((r) => r.contractUid));
  const lKept = (nextL || []).filter((c) => !deleted.has(c.contractUid));
  const eKept = (nextE || []).filter((c) => !deleted.has(c.contractUid));
  const dirtyL = [
    ...diffRowsByUid(prev.oblLinks || [], lKept, 'linkUid', ensureLinkUid),
    ...(prev.oblLinks || []).filter((c) => deleted.has(c.contractUid)).map((c) => ({ ...c, mod: 'D' })),
  ];
  const dirtyE = [
    ...diffRowsByUid(prev.extRefs || [], eKept, 'extRefUid', ensureExtUid),
    ...(prev.extRefs || []).filter((c) => deleted.has(c.contractUid)).map((c) => ({ ...c, mod: 'D' })),
  ];

  const run = async (query, rows) => {
    if (rows?.length) await commonSaveList(query, rows, params);
  };
  await run(SAVE_Q.oblLink, dirtyL.filter((r) => r.mod === 'D'));
  await run(SAVE_Q.extRef, dirtyE.filter((r) => r.mod === 'D'));
  await run(SAVE_Q.contract, dirtyP.filter((r) => r.mod === 'D'));
  await run(SAVE_Q.contract, dirtyP.filter((r) => r.mod === 'A' || r.mod === 'U'));
  await run(SAVE_Q.oblLink, dirtyL.filter((r) => r.mod === 'A' || r.mod === 'U'));
  await run(SAVE_Q.extRef, dirtyE.filter((r) => r.mod === 'A' || r.mod === 'U'));
  return apiLoadContractBundle(tenantCode, fiscalYear);
}
